// src/pages/FicheFrais/components/ExpenseMetaCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "../ui/SectionCard";
import Button from "../../../../components/Button/Button.jsx";
import TravelFields from "./TravelFields";
import CarpoolFields from "./CarpoolFields";

import { FaMoneyCheckAlt, FaRoad, FaSyncAlt } from "react-icons/fa";
import { getBudgetCodes } from "../../../../services/expenseServices";

/* helpers */
const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

function toLabel(row) {
  const code = row?.code || row?.code_budgetaire || row?.budget_code || "";
  const lib = row?.libelle || row?.label || row?.name || "";
  return code && lib ? `${code} — ${lib}` : code || lib || `Code #${row?.id}`;
}

export default function ExpenseMetaCard({
  eventSelected,
  draft,
  setDraft,
  onComputeDistance,
  computingDistance = false,
  residenceAddress = "",
}) {
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [errorCodes, setErrorCodes] = useState("");

  const loadCodes = async () => {
    try {
      setErrorCodes("");
      setLoadingCodes(true);
      const data = await getBudgetCodes();
      setCodes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErrorCodes("Impossible de charger les codes budgétaires.");
      setCodes([]);
    } finally {
      setLoadingCodes(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  // ✅ Mode UI dérivé des champs DB
  const mode = !draft?.trajet_necessaire
    ? "none"
    : draft?.covoiturage
      ? "carpool"
      : "travel";

  // petite recherche optionnelle côté UI
  const [q, setQ] = useState("");
  const filteredCodes = useMemo(() => {
    if (!q.trim()) return codes;
    const needle = normalize(q);
    return codes.filter((r) => normalize(toLabel(r)).includes(needle));
  }, [codes, q]);

  const disabledBecauseNoEvent = !eventSelected?.event_id;

  const setMode = (nextMode) => {
    setDraft((p) => {
      // base reset commun
      if (nextMode === "none") {
        return {
          ...p,
          trajet_necessaire: false,
          moyen_transport: null,
          aller_retour: true,
          depart: "",
          arrivee: "",
          distance_km: "",

          covoiturage: false,
          covoiturage_role: "",
          covoiturage_nb_passagers: 0,
          covoiturage_avec: "",
        };
      }

      if (nextMode === "travel") {
        return {
          ...p,
          trajet_necessaire: true,
          // par défaut voiture (tu peux laisser l’utilisateur changer après)
          moyen_transport: p.moyen_transport || "voiture",
          covoiturage: false,
          covoiturage_role: "",
          covoiturage_nb_passagers: 0,
          covoiturage_avec: "",
          // optionnel : si arrivée vide, préremplir avec lieu event
          arrivee: p.arrivee || eventSelected?.lieu_event || p.arrivee,
        };
      }

      // carpool
      return {
        ...p,
        trajet_necessaire: true,
        moyen_transport: "voiture", // covoiturage => voiture obligatoire
        covoiturage: true,
        // optionnel : si arrivée vide, préremplir avec lieu event
        arrivee: p.arrivee || eventSelected?.lieu_event || p.arrivee,
      };
    });
  };

  const setMoyenTransport = (val) => {
    setDraft((p) => {
      const next = {
        ...p,
        trajet_necessaire: true,
        moyen_transport: val || null,
      };

      // si plus voiture => covoiturage impossible, on reset
      if (val !== "voiture") {
        next.covoiturage = false;
        next.covoiturage_role = "";
        next.covoiturage_nb_passagers = 0;
        next.covoiturage_avec = "";
      }
      return next;
    });
  };

  return (
    <SectionCard
      id="meta"
      title="Informations de la fiche"
      subtitle="Choisissez le code budgétaire et indiquez votre mode de déplacement. Certains champs s’affichent selon votre choix."
      icon={<FaMoneyCheckAlt />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={loadingCodes ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={loadCodes}
            icon={FaSyncAlt}
          />
        </div>
      }
    >
      {disabledBecauseNoEvent ? (
        <div className="rounded-xl p-4 bg-amber-50 text-amber-900 ring-1 ring-amber-200">
          Sélectionne d’abord un <strong>événement</strong> pour continuer.
        </div>
      ) : null}

      {/* ====== Code budgétaire ====== */}
      <div
        className={[
          "mt-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4",
          disabledBecauseNoEvent ? "opacity-60 pointer-events-none" : "",
        ].join(" ")}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <FaMoneyCheckAlt className="text-slate-500" />
            Code budgétaire *
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un code…"
            className="h-9 px-3 rounded-xl ring-1 ring-slate-200 bg-white text-sm w-full md:w-[280px] dark:text-gray-800"
          />
        </div>

        {errorCodes ? (
          <div className="mt-3 rounded-xl p-3 bg-rose-50 text-rose-800 ring-1 ring-rose-200">
            {errorCodes}
          </div>
        ) : null}

        <div className="mt-3">
          <select
            value={draft?.code_budgetaire_id || ""}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                code_budgetaire_id: e.target.value
                  ? Number(e.target.value)
                  : null,
              }))
            }
            className="w-full rounded-xl px-3 py-2 ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-800"
          >
            <option value="">
              {loadingCodes ? "Chargement..." : "— Sélectionner un code —"}
            </option>
            {filteredCodes.map((r) => (
              <option key={r.id} value={r.id}>
                {toLabel(r)}
              </option>
            ))}
          </select>

          <div className="text-xs text-slate-500 mt-2">
            L’événement sélectionné : <strong>{eventSelected?.titre}</strong>
          </div>
        </div>
      </div>

      {/* ====== Déplacement ====== */}
      <div
        className={[
          "mt-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4",
          disabledBecauseNoEvent ? "opacity-60 pointer-events-none" : "",
        ].join(" ")}
      >
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <FaRoad className="text-slate-500" />
          Déplacement
        </div>

        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {/* Mode */}
          <label className="block">
            <div className="text-sm font-semibold text-slate-700 mb-1">
              Mode *
            </div>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full rounded-xl px-3 py-2 ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-800"
            >
              <option value="none">Aucun</option>
              <option value="travel">Trajet</option>
              <option value="carpool">Covoiturage</option>
            </select>
          </label>

          {/* Résumé */}
          <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Résumé
            </div>
            <div className="mt-1 text-sm text-slate-700">
              {mode === "none"
                ? "Aucun déplacement déclaré."
                : mode === "travel"
                  ? "Trajet déclaré (champs trajet à compléter)."
                  : "Covoiturage déclaré (trajet + infos covoiturage)."}
            </div>
          </div>
        </div>

        {/* Moyen de transport (si trajet actif) */}
        {mode !== "none" ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block">
              <div className="text-sm font-semibold text-slate-700 mb-1">
                Moyen de transport *
              </div>
              <select
                value={draft?.moyen_transport || ""}
                onChange={(e) => setMoyenTransport(e.target.value)}
                className="w-full rounded-xl px-3 py-2 ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-800"
              >
                <option value="">— Sélectionner —</option>
                <option value="voiture">Voiture</option>
                <option value="train">Train</option>
                <option value="transport">Transport</option>
                <option value="autre">Autre</option>
              </select>
              <div className="text-xs text-slate-500 mt-1">
                Le covoiturage est disponible uniquement si “Voiture”.
              </div>
            </label>
          </div>
        ) : null}

        {/* Champs trajet */}
        {mode !== "none" ? (
          <div className="mt-4">
            <TravelFields
              draft={draft}
              setDraft={setDraft}
              eventLieu={eventSelected?.lieu_event || ""}
              onComputeDistance={onComputeDistance}
              computingDistance={computingDistance}
              residenceAddress={residenceAddress}
            />
          </div>
        ) : null}

        {/* Champs covoiturage */}
        {mode === "carpool" ? (
          <div className="mt-4">
            <CarpoolFields draft={draft} setDraft={setDraft} />
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
